/**
 * Promotion chain utilities — shared logic for grouping runtimes by their
 * promotion chains (via promotesTo links).
 *
 * Core principle: `promotesTo` links determine only chain *membership* —
 * never display order. Every chain returned here is tier-sorted
 * (dev → test → stag → prod). A "chain" is 2+ runtimes linked via
 * `promotesTo`; a lone, unlinked runtime is "loose", not a chain.
 *
 * Used by:
 * - DeployMenu (grouped deploy dropdown)
 * - RuntimeChainPicker (chain/loose multi-select)
 * - PromotionChainCard (visual chain display)
 * - CutoverEditor (matrix column filtering)
 */

import type { RuntimeSummary } from '../types';

// Tier order for chain display. Grouping is client-side only — the backend
// serves the plain runtime array and does no chain-building of its own.
const ENV_RANK: Record<string, number> = { dev: 0, test: 1, stag: 2, prod: 3 };

/** Sort a copy of `runtimes` by environment tier: dev → test → stag → prod. */
function sortByTier(runtimes: RuntimeSummary[]): RuntimeSummary[] {
  return [...runtimes].sort(
    (a, b) => (ENV_RANK[a.environment] ?? 99) - (ENV_RANK[b.environment] ?? 99),
  );
}

/**
 * Build promotion chains from a flat runtime list. A chain is 2+ runtimes
 * linked via `promotesTo`. Lone, unlinked runtimes are NOT chains — they're
 * "loose" (see groupRuntimesByChains).
 *
 * Each returned chain is tier-sorted (dev → test → stag → prod). `promotesTo`
 * links only determine membership; display order is always tier rank.
 */
export function buildChains(runtimes: RuntimeSummary[]): RuntimeSummary[][] {
  if (runtimes.length === 0) return [];

  const byId = new Map(runtimes.map((r) => [r.id, r]));
  const incoming = new Set<string>();
  for (const r of runtimes) {
    if (r.promotesTo && byId.has(r.promotesTo)) incoming.add(r.promotesTo);
  }

  // Heads = runtimes that promote to a real target but aren't promoted-to.
  const heads = runtimes.filter(
    (r) => r.promotesTo && byId.has(r.promotesTo) && !incoming.has(r.id),
  );

  const visited = new Set<string>();
  const chains: RuntimeSummary[][] = [];

  for (const head of heads) {
    const members: RuntimeSummary[] = [];
    let cur: RuntimeSummary | undefined = head;
    while (cur && !visited.has(cur.id)) {
      visited.add(cur.id);
      members.push(cur);
      cur = cur.promotesTo ? byId.get(cur.promotesTo) : undefined;
    }
    if (members.length >= 2) chains.push(sortByTier(members));
  }

  // Stable output: order chains by their lowest-tier member, then by name.
  chains.sort(
    (a, b) =>
      (ENV_RANK[a[0]!.environment] ?? 99) - (ENV_RANK[b[0]!.environment] ?? 99) ||
      a[0]!.name.localeCompare(b[0]!.name),
  );

  return chains;
}

/**
 * Flat list of every runtime that belongs to a promotion chain, tier-sorted
 * within each chain. Used by CutoverEditor to pick the matrix columns —
 * loose and pipe-orphan runtimes are excluded.
 */
export function filterPromotionChain(runtimes: RuntimeSummary[]): RuntimeSummary[] {
  return buildChains(runtimes).flat();
}

/**
 * Group runtimes into chains + loose: { chains: [{head, chain}], loose }.
 * Built here from the plain `GET /api/runtimes` array.
 * A runtime not part of any 2+ chain is loose. `head` is the chain's
 * lowest-tier member (chains are tier-sorted).
 */
export function groupRuntimesByChains(runtimes: RuntimeSummary[]): {
  chains: Array<{ head: RuntimeSummary; chain: RuntimeSummary[] }>;
  loose: RuntimeSummary[];
} {
  const chains = buildChains(runtimes);
  const inChain = new Set<string>();
  for (const chain of chains) {
    for (const r of chain) inChain.add(r.id);
  }
  const groupedChains = chains.map((chain) => ({ head: chain[0]!, chain }));
  const loose = sortByTier(runtimes.filter((r) => !inChain.has(r.id)));
  return { chains: groupedChains, loose };
}
